import { MINIMUM_PORT, DEFAULT_PORT, HOOK_PATH_MATCHING, MAXIMUM_PORT } from './http-hook.enum'
import { SessionManager } from './session-manager.class'

const urlencode = require('urlencode')

export class HttpHook {
  constructor(basePort = DEFAULT_PORT) {
    this.basePort = basePort
    this._initialize()
  }
  
  //#region public methods
  /**
   * @typedef HookDataSetting
   * @property {string} [validation=hsid] - none | hsid, query param 에 포함된 값과 일치해야 유효한 요청으로 판단
   * @property {string} [httpPath=/] - http://127.0.0.1:${port} 이후 URL path
   * @property {string} [httpPathMatching=FULL] - 설정 path 와 hook 한 path 의 매칭 정도
   * @property {string} [httpMethod=GET] - GET 만 구현 되어 있음
   */
  /**
   *  가능한 port 를 찾아서 HTTP hook 을 준비 (TCP Server open) 합니다
   * 
   * @param {Object} param 
   * @param {Object} param.port - tcp open 시도 하는 포트 (9201 ~ 65535)
   * @param {Object} param.moduleName - 요청하는 모듈 이름 ex) inpay
   * @param {HookCallbackFunction} param.listener - 응답이 왔을 때 호출 될 callback
   * @param {HookDataSetting | HookDataSetting[]} param.hookDataSettings - hook 관련 설정
   * @returns {{ moduleName: string, hsid: number, port: number } |
   *           { error: Error, moduleName: string, userReqPort: number, tryPort: number }}
   */
  async startHttpHook({ moduleName = `name-${Math.random()}`, port: userReqPort, hookDataSettings, listener } = {}) {
    userReqPort = userReqPort || this.basePort
    if (typeof listener !== 'function') {
      return { error: new Error('listener is not function') }
    } else if (userReqPort < MINIMUM_PORT || userReqPort > MAXIMUM_PORT) {
      return { error: new Error(`port must be in from ${MINIMUM_PORT} to ${MAXIMUM_PORT}`) }
    }
    
    if (!Array.isArray(hookDataSettings)) {
      const { httpMethod = 'GET', httpPath = '/', httpPathMatching = HOOK_PATH_MATCHING.FULL, validation = 'hsid' } = hookDataSettings || {}
      hookDataSettings = [{
        ...hookDataSettings,
        httpMethod, httpPath, httpPathMatching, validation,
      }]
    }

    try {
      const { port, sessionId: hsid } = await this.sessionManager.createSession({
        moduleName,
        port: userReqPort,
        listener: this._handleHttpRequest.bind(this, { listener, hookDataSettings, moduleName }),
      })
      return { moduleName, hsid, port, }

    } catch (errorObj) {
      const { port, error } = errorObj
      return { error, moduleName, userReqPort, tryPort: port }
    }
  }

  /**
   *  TCP Server 를 종료합니다.
   * moduleName 만 지정될 경우 일치하는 모든 hook 을, hsid 가 함께 지정 될 경우 해당 ID 의 hook 을 중단합니다.
   * 
   * @param {Object} param
   * @param {string} param.moduleName
   * @param {string} [param.hsid]
   * @returns {{ deleted: boolean, hsids: string[], error: Error }}
   */
  async stopHttpHook({ moduleName, hsid }) {
    return await this.sessionManager.removeSession(moduleName, hsid)
  }

  async stopAllHttpHook() {
    await this.sessionManager.removeAllSession()
  }

  getSessionCount() {
    return this.sessionManager.getSessionCount()
  }
  //#endregion


  //#region private methods
  _initialize() {
    this.sessionManager = new SessionManager()
  }
  /**
   *  모든 Tcp Server 의 Data 를 모아서 처리합니다
   * 
   * @param {Object} closureData
   * @param {Function} listener - 사용자가 전달한 Http Hook callback listener
   * @param {HookDataSetting[]} hookDataSettings - 사용자가 설정한 Http Hook 설정
   * @param {Object} CallbackData
   * @param {number} port - where data from
   * @param {string} data - tcp payload
   */
  async _handleHttpRequest({ listener, hookDataSettings, moduleName }, { sessionId, port, data }) {
    try {
      const scheme = data.toString().split('\n')[0] || ''
      const [method, fullUri = '', _protocol = ''] = scheme.split(' ')
      const [path, paramString = ''] = fullUri ? fullUri.split('?') : []

      if (!fullUri) {
        return
      }

      const protocol = _protocol.endsWith('\r') ? _protocol.substring(0, _protocol.length - 1) : _protocol
      const hookDataSetting = this._findMatchingSetting({ method, path, hookDataSettings })
      if (hookDataSetting) {
        const paramsRaw = this._parseParams(paramString)
        const charset = paramsRaw['charset'] || 'utf-8'
        const params = Object.keys(paramsRaw).reduce((result, k) => {
          try {
            const decodeKey = urlencode.decode(k, charset)
            const decodeValue = urlencode.decode(paramsRaw[k] || '', charset)
            result[decodeKey] = decodeValue
          } catch (e) {}
          return result
        }, {})
        
        const validation = this._validateHookDataSettingValidation({ hookDataSetting, hsid: sessionId, params })
        if (validation && typeof listener === 'function') {
          /** @type { { stopHttpHook: boolean, httpResponseString: string }|null } */
          const cbResult = await listener({ port, method, fullUri, protocol, path, params, hookDataSetting })
          process.nextTick(() => {
            if (cbResult && cbResult.stopHttpHook) {
              this.sessionManager.removeSession(moduleName, sessionId)
            }
          })
          return cbResult
        }
      }

    } catch(error) {
      if (typeof listener === 'function') {
        const cbResult = await listener({ error })
        process.nextTick(() => {
          if (cbResult && cbResult.stopHttpHook) {
            this.sessionManager.removeSession(moduleName, sessionId)
          }
        })
        return cbResult
      }
    }
  }

  /**
   * @returns {HookDataSetting}
   */
  _findMatchingSetting({ method, path, hookDataSettings }) {
    return hookDataSettings.find(setting => {
      const _method = (setting.httpMethod || 'GET').toLocaleUpperCase()
      const _matching = (setting.httpPathMatching || HOOK_PATH_MATCHING.FULL).toLocaleUpperCase()
      const _path = setting.httpPath || '/'

      if (_method !== method) { return false }
      if (_matching === HOOK_PATH_MATCHING.FULL) {
        return _path === path
      } else if (_matching === HOOK_PATH_MATCHING.INCLUDE) {
        return _path.includes(path)
      } else if (_matching === HOOK_PATH_MATCHING.INCLUDED) {
        return path.includes(_path)
      } else {
        return false
      }
    })
  }
  _parseParams(paramString = '') {
    return paramString.split('&').reduce((obj, param) => {
      const [key, value] = param.split('=')
      if (key) {
        obj[key] = value
      }
      return obj
    }, {})
  }
  /**
   * @param {HookDataSetting} hookDataSetting 
   * @param {Object} params
   * @returns {boolean}
   */
  _validateHookDataSettingValidation({ hookDataSetting, hsid, params }) {
    if (hookDataSetting.validation === 'hsid') {
      return params['hsid'] === hsid
    }
    return true
  }
  //#endregion
}
